import { supabase } from '../supabase';

// What a workout_sessions row MEANS, in one place.
//
// The app creates the row the moment a client opens a workout day, before a single set is logged:
// there is no "start workout" button, and the row is where the pre-workout energy rating and skipped
// exercises are saved. So an `in_progress` row is not evidence that any training happened. It may be:
//
//   * a workout under way, or one that was left part-way through (it has completed sets);
//   * a day that was opened and never trained (it has none), which is most of them.
//
// Readers that count rows without telling those apart get the wrong answer. Every function here
// takes `trainedOpenIds`: the ids of `in_progress` sessions that have at least one completed set.

// How long an untouched open session is still "the client may be about to train". Past this it is a
// day that was opened and not trained. WorkoutLogger uses the same window to decide whether an open
// session is resumed or a new one is started.
export const RESUMABLE_SESSION_WINDOW_MS = 6 * 60 * 60 * 1000;

const isOpen = (s) => s.status === 'in_progress' && !s.completed_at;

// Did the client actually train in this session?
export const isTrainedSession = (session, trainedOpenIds) =>
  session.status === 'completed' || (isOpen(session) && trainedOpenIds.has(session.id));

// Does this session count as a workout that was due: done, missed, or started and left unfinished?
// An open session with nothing completed in it does not.
export const isResolvedSession = (session, trainedOpenIds) =>
  !isOpen(session) || trainedOpenIds.has(session.id);

const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const activityTime = (s) => new Date(s.completed_at || s.started_at).getTime();

/**
 * One client's numbers for the coach dashboard.
 *
 * - "Last session" is the last time they TRAINED. An automatically recorded missed day is dated the
 *   end of its week, and an empty session is dated when the app was opened; counting either made a
 *   client who had stopped training look recently active.
 * - Completion rate is completed sessions out of resolved ones. Empty open sessions are left out:
 *   each one used to count as a workout that was not finished.
 *
 * @param {Array} sessions - that client's workout_sessions rows (id, started_at, completed_at, status, energy_rating)
 * @param {Set} trainedOpenIds - ids of in_progress sessions with at least one completed set
 * @param {Date} [now]
 */
export function summariseClientSessions(sessions, trainedOpenIds, now = new Date()) {
  const resolved = (sessions || []).filter((s) => isResolvedSession(s, trainedOpenIds));
  const trained = resolved
    .filter((s) => isTrainedSession(s, trainedOpenIds))
    .sort((a, b) => activityTime(b) - activityTime(a));
  const last = trained[0] || null;
  const lastSessionDate = last ? (last.completed_at || last.started_at) : null;
  const completed = resolved.filter((s) => s.status === 'completed').length;

  return {
    lastSessionDate,
    lastEnergyRating: last?.energy_rating ?? null,
    daysSinceLast: lastSessionDate
      ? Math.round((startOfDay(now) - startOfDay(new Date(lastSessionDate))) / (1000 * 60 * 60 * 24))
      : null,
    completionRate: resolved.length > 0 ? Math.round((completed / resolved.length) * 100) : null,
    completedSessions: completed,
    totalSessions: resolved.length,
    skippedDays: resolved.filter((s) => s.status === 'missed').length,
  };
}

/**
 * At the end of a week, which of its days were missed, and how should that be recorded?
 *
 * A day is accounted for by a completed or missed session, by an open session with a completed set
 * (a real attempt), or by an untouched open session that is still fresh (the client may be about to
 * train). A day whose only session is a STALE, untouched open one was opened and never trained: it
 * is a missed day. It used to be treated as "started", so it was never recorded as missed at all.
 *
 * Such a day is recorded on the row that already exists (`convertSessionIds`) rather than by adding a
 * second row beside it. A day with no session at all gets a new missed row (`inserts`), as before.
 *
 * @returns {{ convertSessionIds: Array, inserts: Array }}
 */
export function planMissedDays({ days, sessions, trainedOpenIds, missedAt, userId, programId, now = new Date() }) {
  const freshCutoff = now.getTime() - RESUMABLE_SESSION_WINDOW_MS;
  const isFresh = (s) => new Date(s.started_at).getTime() >= freshCutoff;
  const accounts = (s) => isResolvedSession(s, trainedOpenIds) || isFresh(s);

  const accountedDayIds = new Set((sessions || []).filter(accounts).map((s) => String(s.day_id)));

  // The stale empty session to turn into the missed record, per day nothing else accounts for.
  // If a day has several, the most recent one is used and the rest are left as they are.
  const staleByDay = new Map();
  for (const s of sessions || []) {
    const dayKey = String(s.day_id);
    if (accounts(s) || accountedDayIds.has(dayKey)) continue;
    const current = staleByDay.get(dayKey);
    if (!current || new Date(s.started_at) > new Date(current.started_at)) staleByDay.set(dayKey, s);
  }

  const dueDays = (days || []).filter((d) => d.exercises?.length > 0);
  const dueDayKeys = new Set(dueDays.map((d) => String(d.id)));

  return {
    convertSessionIds: [...staleByDay.entries()].filter(([dayKey]) => dueDayKeys.has(dayKey)).map(([, s]) => s.id),
    inserts: dueDays
      .filter((d) => !accountedDayIds.has(String(d.id)) && !staleByDay.has(String(d.id)))
      .map((d) => ({
        user_id: userId,
        program_id: programId,
        day_id: d.id,
        started_at: missedAt,
        completed_at: missedAt,
        status: 'missed',
      })),
  };
}

// ── Reads ─────────────────────────────────────────────────────────────────────────────────────────
// Both throw on a failed request. supabase-js resolves a failure as { error } rather than throwing,
// and reading that as "no rows" would show every client as having never trained.

const PAGE = 1000; // PostgREST returns at most this many rows for one request
// Sessions per logs request. Each can return a row per completed set (20 to 30 in a full workout), so
// this keeps one request's answer well under that 1000-row cap, where it would be cut off silently.
const CHUNK = 20;

/** Every workout session for these clients. Paged, because an unpaged request is silently cut off at 1000 rows. */
export async function loadSessionsForClients(authUserIds) {
  if (!authUserIds?.length) return [];
  const sessions = [];
  for (let from = 0; ; from += PAGE) {
    const { data, error } = await supabase
      .from('workout_sessions')
      .select('id, user_id, started_at, completed_at, status, energy_rating')
      .in('user_id', authUserIds)
      .order('id', { ascending: true })
      .range(from, from + PAGE - 1);
    if (error) throw error;
    sessions.push(...(data || []));
    if (!data || data.length < PAGE) return sessions;
  }
}

/** Of the given sessions, the ids of the open ones that have at least one completed set. */
export async function loadTrainedOpenSessionIds(sessions) {
  const openIds = (sessions || []).filter(isOpen).map((s) => s.id);
  const trained = new Set();
  for (let i = 0; i < openIds.length; i += CHUNK) {
    const { data, error } = await supabase
      .from('workout_logs')
      .select('session_id')
      .in('session_id', openIds.slice(i, i + CHUNK))
      .eq('completed', true);
    if (error) throw error;
    (data || []).forEach((log) => trained.add(log.session_id));
  }
  return trained;
}

/**
 * A week the screen shows as finished (every day logged in full or missed) can still be open in the
 * database when a day's sets were all logged but "Complete Day" was never pressed: start-up then keeps
 * sending the client back to that day, and the week never moves on (a client, 17 October 2026: "I've
 * been training but unable to log it as the app is stuck on this page"). These are the open sessions to
 * close, for the days that look done: the newest open one per day, none for a day already closed or
 * missed.
 */
export function openSessionsToClose({ doneDayIds, sessions }) {
  const done = new Set((doneDayIds || []).map(String));
  const closed = new Set((sessions || []).filter((s) => s.completed_at).map((s) => String(s.day_id)));
  const newestOpen = new Map();
  for (const s of sessions || []) {
    const day = String(s.day_id);
    if (s.completed_at || !done.has(day) || closed.has(day)) continue;
    const current = newestOpen.get(day);
    if (!current || new Date(s.started_at) > new Date(current.started_at)) newestOpen.set(day, s);
  }
  return [...newestOpen.values()].map((s) => s.id);
}
